'use strict';

// ─── Directional Hover Fill (shared utility) ───
function initDirectionalHover(el) {
  if (!el) return;
  function dir(e) {
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top;
    const w = r.width, h = r.height;
    const dists = [{ d: 'top', v: y }, { d: 'bottom', v: h - y }, { d: 'left', v: x }, { d: 'right', v: w - x }];
    return dists.reduce((a, b) => a.v < b.v ? a : b).d;
  }
  const offsets = { top: ['0', '-100%'], bottom: ['0', '100%'], left: ['-100%', '0'], right: ['100%', '0'] };
  el.addEventListener('mouseenter', e => {
    const [ox, oy] = offsets[dir(e)];
    el.style.setProperty('--dx', ox);
    el.style.setProperty('--dy', oy);
    requestAnimationFrame(() => el.classList.add('fill-in'));
  });
  el.addEventListener('mouseleave', e => {
    const [ox, oy] = offsets[dir(e)];
    el.classList.remove('fill-in');
    el.style.setProperty('--dx', ox);
    el.style.setProperty('--dy', oy);
  });
}

// ─── Gallery / Product data (Product Page) ───
const ecGallery = (function() {
  let currentSize = 'medium';
  let imgIdx = 0;

  const products = {
    small: {
      name: 'SafetyChew<br>Small Dog',
      sub: 'Starter Pack — From $33.99',
      price: 33.99,
      desc: "Light chewers take their time — and that's when the nub becomes most dangerous. The SafetyChew's pin-lock holds the last inch safely locked no matter how long the session runs.",
    },
    medium: {
      name: 'SafetyChew+<br>Medium Dog',
      sub: 'Starter Pack — From $38.99',
      price: 38.99,
      desc: 'Moderate chewers apply real pressure — the SafetyChew+ is heavy-duty polymer built to hold against sustained chewing. The pin-lock keeps the nub locked safe from the first session to the hundredth.',
    },
    large: {
      name: 'SuperChew<br>Large Dog',
      sub: 'Starter Pack — From $44.99',
      price: 44.99,
      desc: "Power chewers destroy standard holders regardless of size. The SuperChew is USA-manufactured nylon — as hard as a Nylabone — with the same pin-lock mechanism. Built specifically for dogs that have chewed through everything else.",
    },
  };

  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Accepts either the live Shopify media array, the older images-only array,
  // or the hardcoded fallback list of plain URL strings. Anything we cannot
  // render (3D models, external video) is dropped rather than left as a gap.
  function normalizeMedia(list) {
    if (!Array.isArray(list)) return [];
    return list.map(function(m) {
      if (typeof m === 'string') return { type: 'image', src: m, alt: '' };
      if (!m || typeof m !== 'object') return null;
      if (m.type === 'image' && m.src) return m;
      if (m.type === 'video' && Array.isArray(m.sources) && m.sources.length) return m;
      return null;
    }).filter(Boolean);
  }

  // Live product media wins over the fallback list above, so the gallery
  // follows whatever is set on the product in the admin, video included.
  function mediaFor(size) {
    const ep = window.ecProducts && window.ecProducts[size];
    if (!ep) {
      console.error('[EverChew] window.ecProducts missing for "' + size + '" - gallery cannot render.');
      return [];
    }
    const live = normalizeMedia(ep.media);
    if (live.length) return live;
    // images[] carries no video, so this path is a visible degradation, not a fix.
    const imgs = normalizeMedia(ep.images);
    if (imgs.length) {
      console.warn('[EverChew] no media[] for "' + size + '", using images[] - product video will not appear.');
      return imgs;
    }
    console.error('[EverChew] no renderable media for "' + size + '".');
    return [];
  }

  // Shopify serves 480p/720p/1080p mp4. 720p is plenty for a gallery tile and
  // a fraction of the 1080p bitrate, so cap there and fall back to the smallest.
  function pickSource(sources) {
    const sorted = sources.slice().sort((a, b) => a.height - b.height);
    let best = sorted[0];
    for (const s of sorted) if (s.height <= 720) best = s;
    return best;
  }

  function renderGallery() {
    const items = mediaFor(currentSize);
    const thumbStrip = document.getElementById('thumbStrip');
    if (!document.getElementById('mainImg') || !thumbStrip || !items.length) return;
    thumbStrip.innerHTML = items.map((m, i) => {
      const thumbSrc = m.type === 'video' ? m.poster : m.src;
      const cls = 'thumb' + (i === 0 ? ' on' : '') + (m.type === 'video' ? ' is-video' : '');
      const label = m.alt || (m.type === 'video' ? 'Play video' : 'View ' + (i + 1));
      return `<button class="${cls}" onclick="ecGallery.setImg(${i})" aria-label="${esc(label)}"><img src="${esc(thumbSrc)}" alt=""></button>`;
    }).join('');
    setImg(0, true);
  }

  function setImg(i, immediate) {
    const items = mediaFor(currentSize);
    if (!items.length || i < 0 || i >= items.length) return;
    imgIdx = i;
    const m = items[i];
    const mainImg = document.getElementById('mainImg');
    const mainVideo = document.getElementById('mainVideo');
    if (!mainImg) return;
    document.querySelectorAll('.thumb').forEach((t, idx) => t.classList.toggle('on', idx === i));

    if (m.type === 'video' && mainVideo) {
      mainImg.classList.add('ec-hidden');
      mainVideo.classList.remove('ec-hidden');
      if (m.poster) mainVideo.poster = m.poster;
      const src = pickSource(m.sources);
      if (src && mainVideo.getAttribute('src') !== src.url) {
        mainVideo.setAttribute('src', src.url);
        mainVideo.load();
      }
      return;
    }

    if (mainVideo) {
      mainVideo.pause();
      mainVideo.classList.add('ec-hidden');
    }
    mainImg.classList.remove('ec-hidden');
    const apply = () => {
      mainImg.src = m.src;
      mainImg.alt = m.alt || products[currentSize].name.replace('<br>', ' ') + ' — EverChew';
      mainImg.style.opacity = '1';
    };
    if (immediate) { apply(); return; }
    mainImg.style.opacity = '0';
    setTimeout(apply, 80);
  }

  function updatePrices() {
    const base = products[currentSize].price;
    const subPrice = document.getElementById('subPrice');
    const onePrice = document.getElementById('onePrice');
    const atcBtnPrice = document.getElementById('atcBtnPrice');
    if (subPrice) subPrice.textContent = '$' + (base * 0.9).toFixed(2);
    if (onePrice) onePrice.textContent = '$' + base.toFixed(2);
    if (atcBtnPrice) atcBtnPrice.textContent = '$' + base.toFixed(2);
  }

  function selectSize(s) {
    currentSize = s;
    document.querySelectorAll('.sz-btn').forEach(b => b.classList.toggle('on', b.dataset.size === s));
    const p = products[s];
    const bpTitle = document.getElementById('bpTitle');
    const bpSub = document.getElementById('bpSub');
    const bpDesc = document.getElementById('bpDesc');
    const satcName = document.getElementById('satcName');
    const satcPrice = document.getElementById('satcPrice');
    if (bpDesc) bpDesc.textContent = p.desc;
    // Use Shopify product data (title, price, variant ID) — fall back to hardcoded JS values
    if (window.ecProducts && window.ecProducts[s]) {
      const ep = window.ecProducts[s];
      const displayTitle = ep.titleOverride || ep.title || p.name.replace('<br>', ' ');
      if (bpTitle) bpTitle.textContent = displayTitle;
      if (bpSub) bpSub.textContent = ep.sub || p.sub;
      if (satcName) satcName.textContent = displayTitle;
      const hiddenId = document.getElementById('hiddenVariantId');
      if (hiddenId && ep.variantId) hiddenId.value = ep.variantId;
      if (ep.price) products[s].price = ep.price / 100;
    } else {
      if (bpTitle) bpTitle.innerHTML = p.name;
      if (bpSub) bpSub.textContent = p.sub;
      if (satcName) satcName.textContent = p.name.replace('<br>', ' ') + ' Starter Pack';
    }
    if (satcPrice) satcPrice.textContent = 'From $' + p.price.toFixed(2);
    renderGallery();
    updatePrices();
  }

  function step(d) {
    const len = mediaFor(currentSize).length;
    if (!len) return;
    setImg((imgIdx + d + len) % len);
  }

  function init() {
    renderGallery();
    const prevBtn = document.getElementById('prevBtn');
    const nextBtn = document.getElementById('nextBtn');
    if (prevBtn) prevBtn.onclick = () => step(-1);
    if (nextBtn) nextBtn.onclick = () => step(1);
  }

  return { setImg, selectSize, init };
})();

// ─── Purchase Type Toggle ───
window.selectPur = function(p) {
  const subOpt = document.getElementById('subOpt');
  const oneOpt = document.getElementById('oneOpt');
  const delWrap = document.getElementById('delWrap');
  if (subOpt) subOpt.classList.toggle('on', p === 'sub');
  if (oneOpt) oneOpt.classList.toggle('on', p === 'one');
  if (delWrap) delWrap.style.display = p === 'sub' ? 'block' : 'none';
};

// ─── Quantity ───
window.ecQty = (function() {
  let qty = 1;
  function change(d) {
    qty = Math.max(1, qty + d);
    const el = document.getElementById('qtyNum');
    if (el) el.textContent = qty;
    const hiddenQty = document.getElementById('hiddenQty');
    if (hiddenQty) hiddenQty.value = qty;
  }
  return { change, get: () => qty };
})();

// ─── DOM-dependent init (runs after full page is parsed) ───
document.addEventListener('DOMContentLoaded', function() {

  // Scroll Reveal
  const revealObs = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('in');
        revealObs.unobserve(e.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
  document.querySelectorAll('.reveal').forEach(el => revealObs.observe(el));

  // Nav scroll
  const nav = document.getElementById('nav');
  if (nav) {
    window.addEventListener('scroll', () => {
      nav.classList.toggle('scrolled', window.scrollY > 20);
    }, { passive: true });
  }

  // Spotlight card effect
  document.querySelectorAll('.spotlight').forEach(card => {
    card.addEventListener('mousemove', e => {
      const r = card.getBoundingClientRect();
      card.style.setProperty('--mx', (e.clientX - r.left) + 'px');
      card.style.setProperty('--my', (e.clientY - r.top) + 'px');
    });
  });

  // Directional hover on CTA/ATC buttons
  document.querySelectorAll('.hero-btn, .bully-cta, .cta-btn, .atc-btn').forEach(initDirectionalHover);

  // Sticky ATC
  const stickyAtc = document.getElementById('stickyAtc');
  const atcBtn = document.getElementById('atcBtn');
  if (stickyAtc && atcBtn) {
    initDirectionalHover(atcBtn);
    const atcObs = new IntersectionObserver(([entry]) => {
      stickyAtc.classList.toggle('show', !entry.isIntersecting);
    }, { threshold: 0 });
    atcObs.observe(atcBtn);
  }

  // Gallery init (product page)
  if (document.getElementById('mainImg')) {
    ecGallery.init();
  }

});
